"use client";
import { useRef, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import ProductList from "@/components/shop/ProductList";
import { fetchProducts } from "@/services/products";
import { ApiMeta } from "@/types/api";
import { useTranslations } from "next-intl";

interface Props {
  initialMeta: ApiMeta;
  filter?: Record<string, any>;
  sort?: string;
}

export default function ProductInfiniteClient({ initialMeta, filter, sort }: Props) {
  const t = useTranslations();
  const pageSize = 20;
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["products", filter, sort],
    queryFn: ({ pageParam = 2 }) => fetchProducts({ page: pageParam, limit: pageSize, filter, sort }),
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (!meta || meta.currentPage >= meta.totalPages) return undefined;
      return meta.currentPage + 1;
    },
    initialPageParam: 2,
    enabled: !!initialMeta && initialMeta.totalPages > 1,
  });

  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchNextPage();
    });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Собираем все товары, кроме первой страницы (она уже отрендерена сервером)
  const products = data?.pages.flatMap(page => page.data) ?? [];

  if (initialMeta.totalItems <= pageSize) {
    return null;
  }

  return (
    <div>
      <ProductList products={products} />
      {hasNextPage && <div ref={loaderRef} style={{ height: 40 }} />}
      {isFetchingNextPage && <p>{t('loading')}</p>}
      {status === "error" && <p>{t('error_products')}</p>}
    </div>
  );
}